"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.occurrenceBindingForDeclaredBusMember = occurrenceBindingForDeclaredBusMember;
exports.coversCompleteDeclaredBusRange = coversCompleteDeclaredBusRange;
exports.busPortMembersFromOccurrenceBindings = busPortMembersFromOccurrenceBindings;
exports.parentBasePortOccurrenceIdentity = parentBasePortOccurrenceIdentity;
exports.parentPortOccurrenceBindings = parentPortOccurrenceBindings;
exports.childPortOccurrenceBindings = childPortOccurrenceBindings;
exports.childPortMemberOccurrenceBindings = childPortMemberOccurrenceBindings;
exports.portOccurrenceIdentityKey = portOccurrenceIdentityKey;
const OrcadDsnBusUtils_1 = require("./OrcadDsnBusUtils");
const portOccurrenceNormalization_1 = require("../../../portOccurrenceNormalization");
function occurrenceBindingForDeclaredBusMember(bindings, declarationOrdinal, literalBitIndex, declaredMemberCount) {
    const isCompleteAndAligned = bindings.length === declaredMemberCount &&
        bindings.every((binding, ordinal) => binding.identity.declarationOrdinal === ordinal);
    return isCompleteAndAligned ?
        bindings[declarationOrdinal]
        : bindings.find((binding) => binding.identity.literalBitIndex === literalBitIndex);
}
function coversCompleteDeclaredBusRange(declaredPortName, bindings) {
    const declaredMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(declaredPortName);
    if (declaredMembers.length === 0 || bindings.length !== declaredMembers.length) {
        return false;
    }
    const declaredIndices = new Set(declaredMembers.map((member) => member.index));
    const bindingIndices = bindings.map((binding) => binding.identity.literalBitIndex);
    return (bindingIndices.every((index) => index !== undefined) &&
        new Set(bindingIndices).size === bindingIndices.length &&
        bindingIndices.every((index) => declaredIndices.has(index)));
}
function busPortMembersFromOccurrenceBindings(declaredPortName, bindings) {
    const declaredMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(declaredPortName);
    if (!coversCompleteDeclaredBusRange(declaredPortName, bindings)) {
        return declaredMembers;
    }
    return bindings.map((binding) => ({
        name: binding.parentMemberName ?? binding.childPortName,
        index: binding.identity.literalBitIndex,
    }));
}
function valueKey(value) {
    return String(value).toUpperCase();
}
function isMemberOccurrence(occurrence) {
    return occurrence.RefBusMemberName !== '';
}
function literalBitIndex(name) {
    const match = name.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : undefined;
}
function declarationOrdinalForMember(occurrences, member, fallbackOrdinal) {
    const bases = occurrences.filter((row) => !isMemberOccurrence(row) && valueKey(row.RefKey) === valueKey(member.RefKey));
    if (bases.length !== 1) {
        return fallbackOrdinal;
    }
    const ordinal = (0, portOccurrenceNormalization_1.expandBusPortName)(String(bases[0].RefPortName)).findIndex((declared) => valueKey(declared.name) === valueKey(member.RefBusMemberName));
    return ordinal >= 0 ? ordinal : fallbackOrdinal;
}
function boundaryChild(parent, sheetSymbolDbId, reportDiagnostic) {
    const children = parent.children.filter((child) => 'instOccurence' in child && child.sheetSymbol?.DbId.toString() === sheetSymbolDbId);
    const paths = new Set(children.map((child) => child.pathUniqueId));
    const occurrences = new Set(children.map((child) => child.instOccurence));
    if (children.length === 0 || paths.size !== 1 || occurrences.size !== 1) {
        reportDiagnostic(`[hierarchy-port-boundary-ambiguous] parent=${parent.pathUniqueId} sheetSymbol=${sheetSymbolDbId} children=${children.length}`);
        return undefined;
    }
    return children[0];
}
function baseRowsForKey(occurrences, portKey) {
    const requestedKey = valueKey(portKey);
    const keyed = occurrences.filter((occurrence) => !isMemberOccurrence(occurrence) && valueKey(occurrence.RefKey) === requestedKey);
    return keyed.length > 0 ?
        keyed
        : occurrences.filter((occurrence) => !isMemberOccurrence(occurrence) && valueKey(occurrence.RefPortName) === requestedKey);
}
function childDeclarationNames(children) {
    const ports = children.flatMap((child) => child.hierarchyEntry.page?.Port ?? []);
    const netMembers = children.flatMap((child) => (child.hierarchyEntry.page?.NetBus ?? []).flatMap((bus) => (bus.NetScalar ?? []).map((member) => member.Name)));
    if (ports.length === 0 && netMembers.length === 0) {
        return undefined;
    }
    const names = new Set();
    for (const port of ports) {
        names.add(valueKey(port.Name));
        for (const member of (0, portOccurrenceNormalization_1.expandBusPortName)(port.Name)) {
            names.add(valueKey(member.name));
        }
    }
    for (const memberName of netMembers) {
        names.add(valueKey(memberName));
    }
    return names;
}
function hasAmbiguousMemberForBase(base, occurrences) {
    const names = new Set((0, portOccurrenceNormalization_1.expandBusPortName)(String(base.RefPortName)).map((member) => valueKey(member.name)));
    return occurrences.some((row) => row.KeyType === 'AmbiguousPortMember' && isMemberOccurrence(row) && names.has(valueKey(row.RefBusMemberName)));
}
function parentBasePortOccurrenceIdentity(parent, sheetSymbolDbId, portKey, reportDiagnostic = () => undefined) {
    const child = boundaryChild(parent, sheetSymbolDbId, reportDiagnostic);
    if (!child) {
        return undefined;
    }
    const bases = baseRowsForKey(child.instOccurence.PortOccurrence ?? [], portKey);
    if (bases.length !== 1) {
        reportDiagnostic(`[hierarchy-port-unresolved] boundary=${child.pathUniqueId} port=${String(portKey)} bases=${bases.length}`);
        return undefined;
    }
    return { boundaryPath: child.pathUniqueId, refKey: valueKey(bases[0].RefKey) };
}
function parentPortOccurrenceBindings(parent, sheetSymbolDbId, portKey, declaredPortName, reportDiagnostic = () => undefined) {
    const child = boundaryChild(parent, sheetSymbolDbId, reportDiagnostic);
    if (!child) {
        return [];
    }
    const occurrences = child.instOccurence.PortOccurrence ?? [];
    const bases = baseRowsForKey(occurrences, portKey);
    if (bases.length !== 1) {
        reportDiagnostic(`[hierarchy-port-unresolved] boundary=${child.pathUniqueId} port=${declaredPortName} bases=${bases.length}`);
        return [];
    }
    const base = bases[0];
    const refKey = valueKey(base.RefKey);
    const members = occurrences.filter((occurrence) => isMemberOccurrence(occurrence) && valueKey(occurrence.RefKey) === refKey);
    if (members.length === 0 && hasAmbiguousMemberForBase(base, occurrences)) {
        reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${child.pathUniqueId} port=${declaredPortName} reason=ambiguous-member-owner`);
        return [];
    }
    if (members.length > 0) {
        const declaredMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(declaredPortName);
        const declaredByName = new Map(declaredMembers.map((member, ordinal) => [valueKey(member.name), { member, ordinal }]));
        const physicalParentNames = new Set((parent.hierarchyEntry.page ?
            ((0, OrcadDsnBusUtils_1.findNetBusMemberDimensions)(parent.hierarchyEntry.page, declaredPortName) ?? [])
            : []).map((member) => valueKey(member.name)));
        const boundaryPages = parent.children.filter((candidate) => 'instOccurence' in candidate &&
            candidate.pathUniqueId === child.pathUniqueId &&
            candidate.instOccurence === child.instOccurence);
        const childNames = childDeclarationNames(boundaryPages);
        const invalidParent = members.filter((member) => declaredMembers.length > 0 &&
            !declaredByName.has(valueKey(member.RefBusMemberName)) &&
            !physicalParentNames.has(valueKey(member.RefBusMemberName)));
        const invalidChild = members.filter((member) => childNames !== undefined && !childNames.has(valueKey(member.RefPortName)));
        if (invalidParent.length > 0 || invalidChild.length > 0) {
            reportDiagnostic(`[hierarchy-port-unresolved] boundary=${child.pathUniqueId} port=${declaredPortName} reason=invalid-explicit-members parent=${invalidParent[0]?.RefBusMemberName ?? '-'} child=${invalidChild[0]?.RefPortName ?? '-'}`);
            return [];
        }
        const bindings = members.map((member, fallbackOrdinal) => {
            const declared = declaredByName.get(valueKey(member.RefBusMemberName));
            return {
                identity: {
                    boundaryPath: child.pathUniqueId,
                    refKey,
                    declarationOrdinal: declared?.ordinal ?? declarationOrdinalForMember(occurrences, member, fallbackOrdinal),
                    literalBitIndex: declared?.member.index ?? literalBitIndex(member.RefBusMemberName),
                },
                parentMemberName: member.RefBusMemberName,
                childPortName: String(member.RefPortName),
            };
        });
        const ordered = declaredMembers.flatMap((declaredMember) => {
            const match = bindings.find((binding) => valueKey(binding.parentMemberName ?? '') === valueKey(declaredMember.name));
            return match ? [match] : [];
        });
        return ordered.length === bindings.length ? ordered : bindings;
    }
    const baseMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(String(base.RefPortName));
    const compatibilityMembers = baseMembers.length > 0 ? baseMembers : (0, portOccurrenceNormalization_1.expandBusPortName)(declaredPortName);
    if (compatibilityMembers.length > 0) {
        return compatibilityMembers.map((member, declarationOrdinal) => ({
            identity: { boundaryPath: child.pathUniqueId, refKey, declarationOrdinal, literalBitIndex: member.index },
            parentMemberName: member.name,
            childPortName: member.name,
        }));
    }
    return [{ identity: { boundaryPath: child.pathUniqueId, refKey }, childPortName: String(base.RefPortName) }];
}
function childPortOccurrenceBindings(instance, declaredPortName, reportDiagnostic = () => undefined) {
    if (instance.path.length === 0) {
        return [];
    }
    const occurrences = instance.instOccurence.PortOccurrence ?? [];
    const memberRows = occurrences.filter((occurrence) => isMemberOccurrence(occurrence) && valueKey(occurrence.RefPortName) === valueKey(declaredPortName));
    const explicitBindings = memberRows.flatMap((memberRow) => {
        const siblings = occurrences.filter((occurrence) => isMemberOccurrence(occurrence) && valueKey(occurrence.RefKey) === valueKey(memberRow.RefKey));
        const siblingOrdinal = siblings.indexOf(memberRow);
        const declarationOrdinal = declarationOrdinalForMember(occurrences, memberRow, siblingOrdinal);
        return declarationOrdinal < 0 ?
            []
            : [
                {
                    identity: {
                        boundaryPath: instance.pathUniqueId,
                        refKey: valueKey(memberRow.RefKey),
                        declarationOrdinal,
                        literalBitIndex: literalBitIndex(memberRow.RefBusMemberName),
                    },
                    memberName: memberRow.RefBusMemberName,
                },
            ];
    });
    if (explicitBindings.length === 1) {
        return explicitBindings;
    }
    if (explicitBindings.length > 1) {
        reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${instance.pathUniqueId} port=${declaredPortName} explicit=${explicitBindings.length}`);
        return [];
    }
    const baseRows = occurrences.filter((occurrence) => !isMemberOccurrence(occurrence) && valueKey(occurrence.RefPortName) === valueKey(declaredPortName));
    if (baseRows.length === 1) {
        return [{ identity: { boundaryPath: instance.pathUniqueId, refKey: valueKey(baseRows[0].RefKey) } }];
    }
    const compatible = [];
    for (const base of occurrences.filter((occurrence) => !isMemberOccurrence(occurrence))) {
        const refKey = valueKey(base.RefKey);
        if (occurrences.some((row) => isMemberOccurrence(row) && valueKey(row.RefKey) === refKey) ||
            hasAmbiguousMemberForBase(base, occurrences)) {
            continue;
        }
        const members = (0, portOccurrenceNormalization_1.expandBusPortName)(String(base.RefPortName));
        const declarationOrdinal = members.findIndex((member) => valueKey(member.name) === valueKey(declaredPortName));
        if (declarationOrdinal >= 0) {
            compatible.push({
                identity: {
                    boundaryPath: instance.pathUniqueId,
                    refKey,
                    declarationOrdinal,
                    literalBitIndex: members[declarationOrdinal].index,
                },
                memberName: members[declarationOrdinal].name,
            });
        }
    }
    if (compatible.length !== 1 && compatible.length > 0) {
        reportDiagnostic(`[hierarchy-port-ambiguous] boundary=${instance.pathUniqueId} port=${declaredPortName} compatible=${compatible.length}`);
    }
    if (compatible.length === 0) {
        reportDiagnostic(`[hierarchy-port-unresolved] boundary=${instance.pathUniqueId} port=${declaredPortName} reason=no-occurrence-binding`);
    }
    return compatible.length === 1 ? compatible : [];
}
function childPortMemberOccurrenceBindings(instance, declaredPortName, reportDiagnostic = () => undefined) {
    const occurrences = instance.instOccurence.PortOccurrence ?? [];
    const bases = occurrences.filter((row) => !isMemberOccurrence(row) && valueKey(row.RefPortName) === valueKey(declaredPortName));
    if (bases.length !== 1) {
        reportDiagnostic(bases.length > 1 ?
            `[hierarchy-port-ambiguous] boundary=${instance.pathUniqueId} port=${declaredPortName} bases=${bases.length}`
            : `[hierarchy-port-unresolved] boundary=${instance.pathUniqueId} port=${declaredPortName} bases=0`);
        return [];
    }
    const refKey = valueKey(bases[0].RefKey);
    const members = occurrences.filter((row) => isMemberOccurrence(row) && valueKey(row.RefKey) === refKey);
    return members.map((member, fallbackOrdinal) => ({
        identity: {
            boundaryPath: instance.pathUniqueId,
            refKey,
            declarationOrdinal: declarationOrdinalForMember(occurrences, member, fallbackOrdinal),
            literalBitIndex: literalBitIndex(member.RefBusMemberName),
        },
        memberName: String(member.RefPortName),
    }));
}
function portOccurrenceIdentityKey(identity) {
    return `${identity.boundaryPath}\u0000${identity.refKey}\u0000${identity.declarationOrdinal ?? 'base'}\u0000${identity.literalBitIndex ?? 'scalar'}`;
}
//# sourceMappingURL=OrcadDsnPortOccurrenceUtils.js.map